import styled, { css } from 'styled-components';

import type { SEUKTheme } from '@seuk/design-system/theme';

import { Flexbox } from '../../atoms/';

export const ComponentContainer = styled.div``;

export const StyledInputContainer = styled.div<{
  disabled?: boolean;
  hasAutocomplete?: boolean;
}>`
  ${({ theme, hasAutocomplete, disabled }) => css`
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    flex: 1 1;
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    border-radius: ${theme.radius} ${theme.radius} ${hasAutocomplete ? 0 : theme.radius}
      ${hasAutocomplete ? 0 : theme.radius};
    margin: 0;
    padding: 0;

    &:hover {
      background-color: ${disabled ? theme.palette.text.onSurface.inactive : theme.palette.focus.subtle};
    }
  `}
`;

export const StyledInput = styled.input<{
  hasAutocomplete?: boolean;
}>`
  ${({ theme, hasAutocomplete }) => css`
    display: block;
    flex: 1 1;
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    overflow: hidden;
    padding: ${theme.spacing(3)};
    border-radius: ${theme.radius} ${theme.radius} ${hasAutocomplete ? 0 : theme.radius}
      ${hasAutocomplete ? 0 : theme.radius};
    font-family: ShellBook;
    font-size: 16px;
    line-height: 24px; // TODO: Do this in typography
    margin: 0; // Safari's default styling has margins on this, Chrome's doesn't.
    text-align: inherit;

    color: ${theme.palette.text.onSurface.strong};

    &[type='number'] {
      -moz-appearance: textfield;
      -webkit-appearance: textfield;
      appearance: textfield;
    }
    &[type='number']::-webkit-inner-spin-button,
    &[type='number']::-webkit-outer-spin-button,
    &[type='number']:hover::-webkit-inner-spin-button,
    &[type='number']:hover::-webkit-outer-spin-button {
      -moz-appearance: none;
      -webkit-appearance: none;
      appearance: none;
      margin: 0;
    }
  `}
`;

export const Hint = styled.small`
  display: block;
  font-size: 14px;
  line-height: 22px;
  font-family: ShellBook;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

export const ErrorText = styled.div(({ theme }) => ({
  color: theme.palette.accent.secondary.strong,
  marginTop: theme.spacing(1),
  fontFamily: 'ShellBook',
  fontSize: '14px',
  lineHeight: '20px',
}));

export const ErrorIconContainer = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(3)} 0;
`;

export const AutocompleteInputContainer = styled(Flexbox)<{
  disabled?: boolean;
  error?: boolean;
  focused?: boolean;
}>`
  &::before {
    content: '';
    position: absolute;
    top: -1px;
    width: 100%;
    height: 1px;
    display: block;
    background-color: ${({ theme }) => theme.palette.text.onSurface.inactive};
  }

  box-shadow: ${({ theme }) => theme.shadow(2)};
  background-color: ${({ theme }) => theme.palette.background.surface};

  position: absolute;
  z-index: 1;
  cursor: default;
  left: -1px;
  top: 100%;
  box-sizing: border-box;
  right: -1px;
  border: 1px solid;
  border-radius: 0 0 ${({ theme }) => theme.radius} ${({ theme }) => theme.radius};
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-content: stretch;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(2)} 0 0;
  overflow: hidden;
`;

const getBorderColor = ({ disabled, error, focused }: BorderColorProps, theme: SEUKTheme) => {
  if (disabled) {
    return theme.palette.border.medium;
  }

  if (error) {
    return theme.palette.system.strong.negative;
  }

  if (focused) {
    return theme.palette.focus.strong;
  }

  return theme.palette.border.strong;
};

interface BorderColorProps {
  disabled?: boolean;
  error?: boolean;
  focused?: boolean;
  hasAutocomplete?: boolean;
}

export const InputContainer = styled.div<
  {
    hasAutocomplete?: boolean;
  } & BorderColorProps
>`
  ${({ theme, focused, disabled, error, hasAutocomplete }) => css`
    position: relative;
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    align-items: center;
    overflow: ${focused && hasAutocomplete ? 'visible' : 'hidden'};
    color: ${theme.palette.text.onSurface.subtle};
    border: 1px solid;
    &&,
    & ${AutocompleteInputContainer} {
      border-color: ${getBorderColor({ disabled, error, focused }, theme)};
    }
    border-radius: ${theme.radius} ${theme.radius} ${focused && hasAutocomplete ? 0 : theme.radius}
      ${focused && hasAutocomplete ? 0 : theme.radius};
    background-color: ${disabled ? theme.palette.text.onSurface.inactive : 'white'};

    &:hover {
      &,
      & ${AutocompleteInputContainer} {
        border-color: ${disabled ? theme.palette.text.onSurface.subtle : theme.palette.focus.strong};
      }
    }
  `}
`;
